<template>
    <div ref="rootEl" class="px-4 py-4">
        <div class="d-flex flex-column ga-2 mb-4">
            <div class="text-body-2 text-medium-emphasis">Effective date: January 16, 2026</div>
            <div class="text-body-2">
                <div><strong>Company:</strong> HOUSE OF OTTO LLC (Arizona)</div>
                <div><strong>Product:</strong> Mix n’ Batch (“Mix n’ Batch”, “we”, “us”)</div>
                <div><strong>Website:</strong> mixnbatch.app</div>
                <div><strong>Support:</strong> support@mixnbatch.app</div>
            </div>
        </div>

        <div class="d-flex flex-column ga-10">
            <!-- 1 -->
            <section id="basics" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">1) The basics</div>
                <p class="text-body-1">By creating an account, accessing, or using Mix n’ Batch, you agree to these Terms.</p>
                <p class="text-body-1 mb-0">If you don’t agree, don’t use the app. (No hard feelings. The universe remains indifferent.)</p>
            </section>

            <!-- 2 -->
            <section id="who" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">2) Who can use Mix n’ Batch</div>
                <p class="text-body-1">Mix n’ Batch is for anyone.</p>
                <p class="text-body-1 mb-0">It’s not intended for children under 13. If you’re under 13, don’t use the service.</p>
            </section>

            <!-- 3 -->
            <section id="account" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">3) Your account</div>
                <p class="text-body-1">You’re responsible for your account and keeping your login secure.</p>
                <p class="text-body-1">Please provide accurate account information (like your email).</p>
                <p class="text-body-1 mb-0">You’re responsible for what happens under your account.</p>
            </section>

            <!-- 4 -->
            <section id="content" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">4) Your content belongs to you</div>
                <p class="text-body-1">
                    You own what you put into Mix n’ Batch — recipes, ingredients, costs, pricing, orders, customer lists, invoices, uploads, notes,
                    and anything else you create or upload (“Your Content”).
                </p>

                <div class="text-subtitle-1 font-weight-bold mt-4 mb-2">Permission you give us (so the app can work)</div>

                <p class="text-body-1">
                    To run the service, you give us permission to host, store, process, and display Your Content — only as needed to provide Mix n’
                    Batch to you.
                </p>
                <p class="text-body-1 mb-0">We’re not claiming ownership. This is just the “we have to move your boxes to store them” permission.</p>
            </section>

            <!-- 5 -->
            <section id="public" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">5) Public sharing and links</div>
                <p class="text-body-1">Mix n’ Batch may let you share certain things publicly (for example, via a public link).</p>
                <p class="text-body-1">If you choose to make something public, you’re responsible for what you share.</p>
                <p class="text-body-1 mb-0">Don’t publish information you don’t want others to see (including sensitive customer data).</p>
            </section>

            <!-- 6 -->
            <section id="sketchy" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">6) Please don’t use Mix n’ Batch for sketchy stuff</div>
                <p class="text-body-1">Keep it lawful and respectful. You agree not to:</p>
                <ul class="mb-4 pl-6">
                    <li class="text-body-1 mb-1">break the law using the service</li>
                    <li class="text-body-1 mb-1">upload malware or try to hack, scrape, or disrupt the service</li>
                    <li class="text-body-1 mb-1">infringe someone else’s intellectual property</li>
                    <li class="text-body-1 mb-1">use the service to harass, threaten, or abuse others</li>
                </ul>
                <p class="text-body-1 mb-0">We’re not trying to be the fun police. We’re trying to keep the app alive.</p>
            </section>

            <!-- 7 -->
            <section id="payments" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">7) Payments, subscriptions, and free trial</div>
                <p class="text-body-1">Mix n’ Batch offers a 14-day free trial with no credit card required.</p>
                <p class="text-body-1">
                    After the trial ends, you’ll need a paid subscription to keep using paid features. Payments are handled by Stripe. We do not store
                    your full card number on our servers.
                </p>

                <div class="text-subtitle-1 font-weight-bold mt-4 mb-2">Renewals and cancellation</div>
                <p class="text-body-1">Subscriptions typically renew automatically unless you cancel.</p>
                <p class="text-body-1">You can cancel through the app (or contact support if you get stuck).</p>
                <p class="text-body-1 mb-4">After cancellation, you’ll retain access until the end of your current billing period.</p>

                <div class="text-subtitle-1 font-weight-bold mt-4 mb-2">Refunds</div>
                <p class="text-body-1">Unless required by law, payments are non-refundable.</p>
                <p class="text-body-1 mb-0">If something went sideways, contact support — we’ll treat you like a human and review it.</p>
            </section>

            <!-- 8 -->
            <section id="third-parties" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">8) Third-party services we rely on</div>
                <p class="text-body-1">
                    To run Mix n’ Batch, we use third-party providers (for example: Stripe for payments, Sentry for error monitoring, Resend for
                    transactional email, and DigitalOcean for hosting).
                </p>
                <p class="text-body-1 mb-0">
                    They may process limited data as needed to provide their services. Their terms and policies also apply to their part of the world.
                </p>
            </section>

            <!-- 9 -->
            <section id="changes" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">9) Availability and changes</div>
                <p class="text-body-1">
                    We work hard to keep Mix n’ Batch running, but we can’t promise it will always be perfect, always available, or always bug-free.
                </p>
                <p class="text-body-1 mb-0">We may change the service over time (add features, remove features, adjust limits, etc.).</p>
            </section>

            <!-- 10 -->
            <section id="termination" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">10) Account suspension and termination</div>
                <p class="text-body-1">
                    We may suspend or terminate accounts that violate these Terms, abuse the service, or create security risks or legal exposure.
                </p>
                <p class="text-body-1 mb-0">If you want your account deleted, email support@mixnbatch.app (details below in “Account deletion”).</p>
            </section>

            <!-- 11 -->
            <section id="as-is" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">11) “As-is” disclaimer</div>
                <p class="text-body-1">Mix n’ Batch is provided “as is” and “as available.”</p>
                <p class="text-body-1">We don’t make guarantees that it will meet every need, be error-free, or never lose data.</p>
                <p class="text-body-1 mb-0">You should keep your own backups of critical business information.</p>
            </section>

            <!-- 12 -->
            <section id="liability" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">12) Limitation of liability</div>
                <p class="text-body-1">To the fullest extent allowed by law:</p>
                <ul class="mb-0 pl-6">
                    <li class="text-body-1 mb-1">
                        We are not liable for indirect or consequential damages (lost profits, lost data, business interruption, etc.).
                    </li>
                    <li class="text-body-1 mb-1">
                        Our total liability for any claim is limited to the greater of 100 USD or the amount you paid us in the last 12 months.
                    </li>
                </ul>
            </section>

            <!-- 13 -->
            <section id="indemnity" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">13) If you cause a mess, you help clean it up</div>
                <p class="text-body-1 mb-0">
                    If your use of Mix n’ Batch causes us legal trouble (for example, you upload infringing content or use the service unlawfully),
                    you agree to cover reasonable costs and claims that result.
                </p>
            </section>

            <!-- 14 -->
            <section id="law" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">14) Governing law and disputes</div>
                <p class="text-body-1">These Terms are governed by Arizona law (excluding conflict-of-law rules).</p>
                <p class="text-body-1 mb-0">
                    Any disputes will be handled in state or federal courts located in Arizona, and you agree to that venue.
                </p>
            </section>

            <!-- 15 -->
            <section id="deletion" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">15) Account deletion</div>
                <p class="text-body-1">Users can’t self-delete inside the app right now. If you want your account deleted:</p>
                <p class="text-body-1">Email support@mixnbatch.app from the address on your account.</p>
                <p class="text-body-1">We will delete your account and associated data within 10 days.</p>
                <p class="text-body-1">Backups may retain data for up to 10 additional days.</p>
                <p class="text-body-1 mb-0">Stripe billing records may remain for accounting/legal reasons.</p>
            </section>

            <!-- 16 -->
            <section id="contact" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">16) Contact</div>
                <p class="text-body-1 mb-0">Questions about these Terms? Email support@mixnbatch.app.</p>
            </section>
        </div>

        <div class="d-flex mt-6 justify-end">
            <v-btn variant="text" @click="scrollToTop">Back to top</v-btn>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { scrollToTopFromElement } from '@/utils/scrollToTopFromElement';
    import { ref } from 'vue';

    const rootEl = ref<HTMLElement | null>(null);

    const scrollToTop = () => {
        scrollToTopFromElement(rootEl.value);
    };
</script>

<style lang="scss" scoped>
    .legal-section {
        scroll-margin-top: 24px;
    }
</style>
